import type { FieldValue } from "firebase/firestore"
import { addDoc, collection, getDocs, limit, query, serverTimestamp, updateDoc, where } from "firebase/firestore"

import { db } from "@/lib/firebase"
import { parseWaitlistCountResponse } from "@/services/waitlist-count-response"

export interface ReferralInvitation {
  id?: string
  waitlistId: string
  referrerEmail: string
  referrerName?: string
  recipientEmail: string
  sentAt: FieldValue | Date
  convertedAt?: FieldValue | Date
  converted: boolean
}

/**
 * Waitlist rows live in the product database and are written through
 * `@/services/waitlist-api` (AUT-236). Firestore is kept only for the
 * referral-invitation emails below.
 */
const REFERRALS_COLLECTION = "referral_invitations"

/**
 * Create referral invitations
 */
export async function createReferralInvitations(
  waitlistId: string,
  referrerEmail: string,
  referrerName: string | undefined,
  recipientEmails: string[]
): Promise<void> {
  try {
    const invitations = recipientEmails.map((recipientEmail) => ({
      waitlistId,
      referrerEmail: referrerEmail.toLowerCase(),
      ...(referrerName && { referrerName }), // Only include if exists
      recipientEmail: recipientEmail.toLowerCase(),
      sentAt: serverTimestamp(),
      converted: false,
    }))

    const promises = invitations.map((invitation) => addDoc(collection(db, REFERRALS_COLLECTION), invitation))

    await Promise.all(promises)
  } catch (error) {
    console.error("Error creating referral invitations:", error)
    throw new Error("Failed to create referral invitations")
  }
}

/**
 * Mark referral invitation as converted
 */
export async function markReferralConverted(recipientEmail: string, _referralCode: string): Promise<void> {
  try {
    // Find the referral invitation
    const q = query(
      collection(db, REFERRALS_COLLECTION),
      where("recipientEmail", "==", recipientEmail.toLowerCase()),
      where("converted", "==", false),
      limit(1)
    )
    const querySnapshot = await getDocs(q)

    if (!querySnapshot.empty) {
      const firstDoc = querySnapshot.docs[0]
      if (firstDoc) {
        const docRef = firstDoc.ref
        await updateDoc(docRef, {
          converted: true,
          convertedAt: serverTimestamp(),
        })
      }
    }
  } catch (error) {
    console.error("Error marking referral as converted:", error)
  }
}

/**
 * Marketing social-proof waitlist total.
 *
 * Layered (no Firebase / no direct Supabase from the browser):
 *   getWaitlistCount() → www /api/waitlist-count (BFF)
 *                     → SaaS /api/public/waitlist-count
 *                     → public-waitlist.service → public.waitlist
 *
 * Throws on network/API/config failure. `WaitlistCountUnavailableError` means
 * the BFF has no SaaS origin (local/CI). Callers should not show the message
 * to end users — fall back to generic social-proof copy (no fake counts).
 */
export async function getWaitlistCount(): Promise<number> {
  const res = await fetch("/api/waitlist-count", {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  })

  const body = (await res.json()) as { count?: unknown; error?: unknown; unavailable?: unknown }

  return parseWaitlistCountResponse(res.status, body)
}

export { WaitlistCountUnavailableError } from "@/services/waitlist-count-response"
